import { IWorkflowEngineDownloadConfig } from "@kuika/kuika-cl-model";
import { Form } from "antd/es";
import classNames from "classnames";
import { useEffect, useState } from "react";
import { ApiManagementService } from "../../../../../services/api-management-service";
import { ConfigService } from "../../../../../services/config-service";
import { WorkflowEngineService } from "../../../../../services/workflow-engine-service";
import { IWithSDProps, withSD } from "../../../../../shared/hoc/screen-design-consumer";
import { KMainFunctions } from "../../../../../shared/utility/main-functions";
import ChevronDownSvg from "../../../../../svgs/small-icons/chevron-down.svg";
import { DefaultModal } from "../../../../common/default-modal";
import { KModalFooter } from "../../../../ui-kit/k-modal/k-modal-footer";
import KuikaInfoBox from "../../../../ui-kit/kuika-info-box/kuika-info-box";
import KuikaSelectbox from "../../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSvg from "../../../../ui-kit/kuika-svg/kuika-svg";
import { failureMessageOr, type ToastId, toastService } from "../../../../ui-kit/kuika-toast/toast-service";
import { ConfigCacheInput } from "../../../app-settings/app-settings-config-manager/app-settings-config-cache/app-settings-config-cache-input";
import {
  ICommonConfigDeleteFunctionParams,
  ICommonConfigSaveFunctionParams
} from "../../../app-settings/app-settings-config-manager/app-settings-config-container";
import { ConfigDBConnectionStringInput } from "../../../app-settings/app-settings-config-manager/app-settings-config-db-connection-string/app-settings-config-db-connection-string-input";
import { ConfigMailInput } from "../../../app-settings/app-settings-config-manager/app-settings-config-mail/app-settings-config-mail-input";
import { ConfigWorkflowEngineBaseUrlInput } from "../../../app-settings/app-settings-config-manager/app-settings-config-workflow-engine-base-url/app-settings-config-workflow-engine-base-url-input";
import "./download-workflow-engine-modal.scss";

interface IDownloadWorkflowEngineModalProps {
  open: boolean;
  onClose: () => void;
}

// Collects the engine's deployment config at DOWNLOAD time (not stored on the export config). Reuses the
// export-config editor's own inputs, so each setting can be picked from an existing one OR created inline.
// JWT/API key are computed during code-gen from the domain and are never asked here.
const DownloadWorkflowEngineModalBase = (props: IDownloadWorkflowEngineModalProps & IWithSDProps): JSX.Element => {
  const [form] = Form.useForm();
  const [configId, setConfigId] = useState<string | undefined>(undefined);
  const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
  const [platformVersion, setPlatformVersion] = useState<string | null>(null);
  const [optionalOpen, setOptionalOpen] = useState<boolean>(false);

  const exportConfigs = (props.sd.configurations ?? []).filter((c) => !c.isRunConfig);
  const projectId: string = props.sd.project?.id;

  // The Kuika release version the server was built with — labels which platform build this engine matches.
  useEffect(() => {
    if (!props.open) {
      return;
    }
    let cancelled: boolean = false;
    void WorkflowEngineService.GetPlatformVersion().then((version: string | null) => {
      if (!cancelled) {
        setPlatformVersion(version);
      }
    });
    return () => {
      cancelled = true;
    };
  }, [props.open]);

  // The reused inputs need no real section descriptions here; the modal has its own copy.
  const getSectionDescription = (): string => "";

  const setFormField = (name: string[], value: string | null): void => {
    form.setFields([{ name, value }]);
  };

  const refreshSettings = (callback: () => void): void => {
    props.sd.fetchConfigs({ shouldFetchGlobals: true, callback });
  };

  const saveCommonConfig = (
    params: ICommonConfigSaveFunctionParams,
    persist: (pid: string, key: string, cfg: unknown) => Promise<unknown>
  ): void => {
    persist(projectId, params.configKey, params.config)
      .then(() => {
        params.onSuccess();
        refreshSettings(() =>
          setFormField(params.formInputName, params.customizedId ?? (params.config as { id: string }).id)
        );
      })
      .catch((error: unknown) => {
        KMainFunctions.exceptionHandler(error);
        params.onError?.();
      })
      .finally(() => params.onFinal());
  };

  const handleCreateCommonConfig = (params: ICommonConfigSaveFunctionParams): void => {
    saveCommonConfig(params, (pid, key, cfg) => ConfigService.createCommonConfig(pid, key, cfg));
  };

  const handleUpdateCommonConfig = (params: ICommonConfigSaveFunctionParams): void => {
    saveCommonConfig(params, (pid, key, cfg) => ConfigService.updateCommonConfig(pid, key, cfg));
  };

  const handleDeleteCommonConfig = (params: ICommonConfigDeleteFunctionParams): void => {
    ConfigService.deleteCommonConfig(projectId, params.configKey, params.configId)
      .then(() => {
        params.onSuccess();
        refreshSettings(() => setFormField(params.formInputName, null));
      })
      .catch((error: unknown) => {
        KMainFunctions.exceptionHandler(error);
        params.onError?.();
      })
      .finally(() => params.onFinal());
  };

  const handleUpdateCustomFormInput = (formItemName: string[], value: string | null): void => {
    setFormField(formItemName, value);
  };

  const inputProps = {
    commonSettings: props.sd.commonSettings ?? {},
    getSectionDescription,
    handleUpdateCustomFormInput,
    handleCreateCommonConfig,
    handleUpdateCommonConfig,
    handleDeleteCommonConfig
  };

  const handleDownload = async (): Promise<void> => {
    const config = exportConfigs.find((c) => c.id === configId);
    if (!config) {
      KMainFunctions.displayWarningNotification("Select a configuration to download the engine for.");
      return;
    }

    // This engine is downloaded to run on the CUSTOMER's own server, so the Kuika-managed "Default" values
    // must never be baked into its appsettings.json. Workflow DB, Engine Base URL and Mail Provider are
    // therefore mandatory (their "Default" option is hidden in this modal). antd surfaces any missing field
    // inline via its rules — bail quietly instead of a generic toast (KOA-18311).
    try {
      await form.validateFields();
    } catch {
      return;
    }

    const api = form.getFieldValue("apiConfig") ?? {};
    const workflowDbId: string = api.workflowDbConnectionStringSettingsId;
    const baseUrlId: string = api.workflowEngineBaseUrlSettingsId;
    const mail: string = api.workflowMailSettingsId; // composite "smtp|<id>" / "aws|<id>"

    const downloadConfig: IWorkflowEngineDownloadConfig = {
      workflowEngineBaseUrlSettingsId: baseUrlId,
      workflowDbConnectionStringSettingsId: workflowDbId,
      // Main DB = the app's business-data DB the engine reads. Left empty means "the app tables live in the
      // same database as the workflow schema", so we point it at the chosen Workflow DB; a customer only
      // picks a separate DB when their app data is stored apart. Never fall through to the managed DB here.
      mainDbConnectionStringSettingsId: api.dbConnectionStringSettingsId ?? workflowDbId,
      // Redis stays optional — the engine defaults to the SQL-backed cache when none is picked.
      cacheSettingsId: api.cacheSettingsId ?? null,
      workflowSmtpSettingsId: mail?.startsWith("smtp|") ? mail.slice("smtp|".length) : null,
      workflowAwsSettingsId: mail?.startsWith("aws|") ? mail.slice("aws|".length) : null
    };

    // Building the engine is a long job the dialog used to wait on in silence, so one toast now
    // carries it from "Preparing" to its outcome.
    let toastId: ToastId | null = null;
    const work: Promise<string> = (async (): Promise<string> => {
      const url: string | null = await WorkflowEngineService.DownloadEngine({
        projectId: props.sd.project?.id,
        selectedConfId: config.id,
        downloadConfig
      });
      if (!url) {
        // `DownloadEngine` reports its own failure in a toast of its own; the progress toast steps
        // aside before the rejection reaches it, rather than say the same thing twice.
        if (toastId !== null) toastService.dismiss(toastId);
        throw new Error("The workflow engine could not be prepared.");
      }
      const cgApiResponse = await ApiManagementService.GetCodeGenerationApiUrl();
      return cgApiResponse.data + url.substring(url.indexOf("/v1/api"));
    })();
    toastId = toastService.promise(work, {
      loading: "Preparing the workflow engine…",
      success: "The workflow engine is ready.",
      error: failureMessageOr("The workflow engine could not be prepared.")
    });

    setIsSubmitting(true);
    try {
      const finished: string = await work;
      const newTab = window.open(finished, "_blank");
      if (newTab == null || typeof newTab === "undefined") {
        alert("Please disable your pop-up blocker.");
      }
      props.onClose();
    } catch {
      // The toast has said why; the dialog stays open with its choices for another try.
    } finally {
      setIsSubmitting(false);
    }
  };

  const field = (labelText: string, control: JSX.Element, hint?: string): JSX.Element => (
    <div className="kuika-wfe-download__field">
      <span className="kuika_form_label k_Medium-G2-Left-2 property__text kuika-wfe-download__field-label">
        {labelText}
      </span>
      <div className="kuika-wfe-download__field-control">{control}</div>
      {hint && <span className="kuika-wfe-download__field-hint">{hint}</span>}
    </div>
  );

  const versionLabel: string | null = (() => {
    if (!platformVersion) {
      return null;
    }
    return platformVersion.startsWith("v") ? platformVersion : `v${platformVersion}`;
  })();

  return (
    <DefaultModal
      firstHeader="Download workflow engine"
      showModal={props.open}
      closeModal={props.onClose}
      width={600}
      dontRenderSpinner
      footer={
        <KModalFooter
          onCancel={props.onClose}
          submitLabel="Download WF Engine"
          onSubmit={() => void handleDownload()}
          loading={isSubmitting}
        />
      }
    >
      <KuikaSelectbox
        searchable
        style={{ width: "100%" }}
        placeholder="Select an export configuration"
        value={configId}
        options={exportConfigs.map((c) => ({ value: c.id, label: c.name }))}
        onChange={(value) => setConfigId(value as string)}
      />

      {versionLabel && (
        <KuikaInfoBox
          color="blue"
          style={{ marginTop: 14 }}
          leftUnit={
            <span className="kuika-wfe-download__version">
              <KuikaSvg name="info-circle" width={16} height={16} />
              <span>
                Engine build <strong>{versionLabel}</strong>
              </span>
            </span>
          }
        />
      )}

      <div className="kuika_app-settings__content__container__config-manager__topic-text kuika-wfe-download__helper">
        Pick the settings this engine build runs with
      </div>

      <Form
        form={form}
        layout="vertical"
        requiredMark={false}
        initialValues={{ apiConfig: { dbConnectionStringSettingsId: null } }}
      >
        {field(
          "Workflow DB Connection String",
          <ConfigDBConnectionStringInput
            {...inputProps}
            isWorkflow
            hideDefaultOption
            requiredMessage="Select the database that stores the workflow tables."
          />,
          "The database the engine keeps its workflow (process) tables in."
        )}
        {field(
          "Engine Base URL",
          <ConfigWorkflowEngineBaseUrlInput
            {...inputProps}
            hideDefaultOption
            requiredMessage="Select the public URL this engine will be reachable at."
          />,
          "The public URL your deployed engine will be served from."
        )}
        {field(
          "Mail Provider",
          <ConfigMailInput
            {...inputProps}
            isWorkflowMailSetting
            hideDefaultOption
            requiredMessage="Select the mail provider the engine sends notification e-mails through."
          />,
          "The engine sends its own workflow notification e-mails through this provider."
        )}

        <button
          type="button"
          className="kuika-wfe-download__optional-toggle"
          aria-expanded={optionalOpen}
          onClick={() => setOptionalOpen((open: boolean): boolean => !open)}
        >
          <span
            className={classNames("kuika-wfe-download__optional-chevron", {
              "kuika-wfe-download__optional-chevron--open": optionalOpen
            })}
          >
            <ChevronDownSvg />
          </span>
          <span>Optional overrides</span>
        </button>
        {optionalOpen && (
          <div className="kuika-wfe-download__optional-content">
            {field(
              "Application DB Connection",
              <ConfigDBConnectionStringInput {...inputProps} defaultOptionLabel="Same as Workflow DB" />,
              'The database holding your application\'s data. Keep "Same as Workflow DB" if your app tables live in the same database as the workflow schema; pick a database only if your app data is stored separately.'
            )}
            {field("Redis / Cache", <ConfigCacheInput {...inputProps} />)}
          </div>
        )}
      </Form>

      <div className="kuika-wfe-download__info">
        <KuikaSvg name="info-circle" width={16} height={16} />
        <span className="kuika_app-settings__content__container__config-manager__topic-text kuika-wfe-download__info-text">
          This engine runs your app&apos;s Process Automation independently of your app code. Required if you&apos;re
          deploying outside our managed cloud — Preview and managed hosting handle it for you automatically.
        </span>
      </div>
    </DefaultModal>
  );
};

const downloadWorkflowEngineModal = withSD<IDownloadWorkflowEngineModalProps>(DownloadWorkflowEngineModalBase);
export { downloadWorkflowEngineModal as DownloadWorkflowEngineModal };
